import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider, useApp } from './contexts/AppContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ConsoleProvider } from './contexts/ConsoleContext';
import { AuthProvider } from './contexts/AuthContext';
import AuthGuard from './components/auth/AuthGuard';
import FluidCanvas from './components/FluidCanvas';
import SimplePaintingCanvas from './components/painting/SimplePaintingCanvas'; // ✨ NOVO: Canvas simplificado
import PaintingCanvas2 from './components/painting/PaintingCanvas2'; // 🎨 NOVO: Pintura2
import LayerPanel from './components/LayerPanel';
import LayerList from './components/LayerList';
import PaperSizePanel from './components/PaperSizePanel';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import AdvancedEditorModal from './components/advanced/AdvancedEditorModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import SidebarControls from './components/SidebarControls';
import MobileTabBar from './components/MobileTabBar';
import MobileControlsPanel from './components/MobileControlsPanel';
import LanguageSelector from './components/LanguageSelector';
import { useTranslation } from './hooks/useTranslation';
import './App.css';
import './styles/BackendIntegration.css';

// 🔥 Lazy load do sistema de efeitos
const EffectsManager = lazy(() => import('./img-effects/EffectsManager'));

// 🔥 Lazy load do sistema de filtros de cores
const ColorFiltersManager = lazy(() => import('./color-filters/ColorFiltersManager'));

// Componente interno que usa o contexto
const AppContent = () => {
  const { t } = useTranslation();
  const {
    isBackgroundRemovalLoading,
    isAdvancedModalOpen,
    advancedEditingImage,
    advancedEditingMask,
    applyAdvancedEdit,
    closeAdvancedEditor,
    isDeleteModalOpen,
    imageToDelete,
    cancelDeleteConfirmation,
    confirmDelete,
    isColorFiltersPanelOpen,
    isSimplePaintingMode, // ✨ NOVO: Modo pintura simplificado
    isPainting2Mode, // 🎨 NOVO: Modo Pintura2
    selectedImageId, // Para o mobile tab bar
    applyEffect,
    closeEffectsPanel,
    setActiveEffectType,
    applyColorFilter,
    setSelectedColorFilter,
    uploadedImages,
    updateImage,
    addImage, // ✨ NOVO: Para carregamento automático
    updateBaseLayer, // ✨ NOVO: Para configurar baseLayer
  } = useApp();


  // 📱 Estados para Mobile
  const [mobileActiveTab, setMobileActiveTab] = useState('geral');

  // 🚀 Carregamento automático da imagem theo-2mb.png
  const [defaultImageLoaded, setDefaultImageLoaded] = React.useState(false);
  
  React.useEffect(() => {
    const loadDefaultImage = async () => {
      // Só carregar se não há imagens, não foi carregada ainda e estamos em desenvolvimento
      const shouldAutoLoad = process.env.REACT_APP_AUTO_LOAD_DEFAULT === 'true';
      if (uploadedImages.length === 0 && !defaultImageLoaded && shouldAutoLoad) {
        setDefaultImageLoaded(true); // Marcar como carregada para evitar múltiplas execuções
        try {
          console.log('🚀 Carregando imagem padrão theo-2mb.png...');
          
          // Criar um File object a partir da imagem
          const response = await fetch('/theo-2mb.png');
          const blob = await response.blob();
          const file = new File([blob], 'theo-2mb.png', { type: 'image/png' });
          
          // Criar um HTMLImageElement para processar
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          await new Promise((resolve, reject) => {
            img.onload = () => {
              console.log('✅ Imagem padrão carregada:', {
                width: img.naturalWidth,
                height: img.naturalHeight,
                name: 'theo-2mb.png'
              });
              resolve();
            };
            img.onerror = reject;
            img.src = URL.createObjectURL(blob);
          });
          
          // Adicionar a imagem ao contexto
          const imageData = {
            id: `image-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: 'theo-2mb.png',
            src: img.src,
            image: img,
            transform: {
              x: 0,
              y: 0,
              scale: 1,
              rotation: 0,
              width: img.naturalWidth,
              height: img.naturalHeight
            },
            opacity: 100,
            blendMode: 'normal',
            isVisible: true
          };
          
          console.log('🔍 Debug imagem antes de adicionar:', {
            id: imageData.id,
            name: imageData.name,
            hasImage: !!imageData.image,
            imageComplete: imageData.image.complete,
            imageNaturalWidth: imageData.image.naturalWidth,
            imageNaturalHeight: imageData.image.naturalHeight,
            imageSrc: imageData.image.src.substring(0, 100),
            transform: imageData.transform
          });
          
          addImage(imageData);
          console.log('🎉 Imagem padrão adicionada ao editor!');
          
          // Configurar a baseLayer com as dimensões da imagem
          updateBaseLayer({
            width: img.naturalWidth,
            height: img.naturalHeight,
            isVisible: true
          });
          console.log('📄 BaseLayer configurada:', {
            width: img.naturalWidth,
            height: img.naturalHeight,
            isVisible: true
          });
          
        } catch (error) {
          console.warn('⚠️ Não foi possível carregar a imagem padrão:', error);
        }
      }
    };
    
    loadDefaultImage();
  }, [uploadedImages.length, addImage, defaultImageLoaded]);

  // Funções de ações mobile
  const handleMobileReset = () => {
    const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
    
    switch (mobileActiveTab) {
      case 'ajustes':
        // Resetar ajustes - volta para a imagem original
        if (selectedImage && selectedImage.originalImage) {
          updateImage(selectedImageId, {
            ...selectedImage,
            image: selectedImage.originalImage,
          });
        }
        closeEffectsPanel();
        break;
      
      case 'filtros':
        // Resetar filtro - volta para a imagem sem filtro
        if (selectedImage && selectedImage.originalImage) {
          updateImage(selectedImageId, {
            ...selectedImage,
            image: selectedImage.originalImage,
            analogFilter: null,
            analogFilterPath: null,
          });
        }
        break;
      
      case 'cores':
        // Resetar cor - remove filtro de cor
        setSelectedColorFilter(null);
        closeEffectsPanel();
        break;
      
      case 'efeitos':
        // Resetar efeito
        setActiveEffectType(null);
        closeEffectsPanel();
        break;
      
      
      default:
        break;
    }
  };

  const handleMobileApply = () => {
    switch (mobileActiveTab) {
      case 'ajustes':
        applyEffect('adjustments');
        break;
      
      case 'filtros':
        // Filtros já são aplicados em tempo real
        break;
      
      case 'cores':
        applyColorFilter();
        break;
      
      case 'efeitos':
        // Aplicar efeito ativo
        if (selectedImageId) {
          applyEffect();
        }
        break;
      
      
      default:
        break;
    }
  };

  return (
    <div className="App h-screen flex flex-col overflow-hidden relative touch-none" style={{ backgroundColor: '#252722' }}>
      {/* Navbar global fixa (cobre toda a largura, acima do sidebar e conteúdo) */}
      <div className="fixed top-0 left-0 right-0 z-50 header-transparent" style={{ backgroundColor: 'transparent', background: 'transparent' }}>
        <div className="px-3 md:px-6 py-2 md:py-3 flex items-center justify-between">
          <img
            src="/pickiwi-logo.webp"
            alt="Pickiwi Logo"
            className="h-8 md:h-10 w-auto"
          />
          <div className="flex items-center space-x-2 md:space-x-4">
            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* Espaço para a navbar fixa */}
      <div className="h-[52px] md:h-[64px]"></div>

      {/* Conteúdo principal - Canvas ocupa toda a área */}
      <div className="flex-1 relative min-h-0">
        {/* Canvas - Ocupa toda a área disponível */}
        <div className="absolute inset-0 w-full h-full">
          {isPainting2Mode ? (
            // 🎨 Modo Pintura2
            <PaintingCanvas2 isActive={true} />
          ) : isSimplePaintingMode ? (
            // ✨ Modo Pintura Simplificado
            <SimplePaintingCanvas isActive={true} />
          ) : (
            // Canvas Normal
            <FluidCanvas />
          )}
        </div>

        {/* Sidebar Esquerdo - Painel de Controles com Abas (DESKTOP ONLY) - Sobreposto */}
        <div 
          className="hidden md:flex fixed left-0 top-[64px] bottom-0 w-80 flex-col overflow-hidden rounded-2xl z-30 backdrop-blur-md"
          style={{ 
            backgroundColor: 'rgba(33, 46, 13, 0.9)',
            boxShadow: 'rgb(0 0 0 / 50%) 0px 0px 16px 0px, inset rgb(32 46 12 / 50%) 0px 0px 32px 0px',
            margin: '8px',
            padding: '8px'
          }}
        >
          <SidebarControls />
        </div>

        {/* Lista de Camadas - Sobreposta no lado direito (Desktop only) */}
        <div className="hidden md:block">
          <LayerList />
        </div>

        {/* 📱 MOBILE - Componentes de controle */}
        <MobileControlsPanel 
          activeTab={mobileActiveTab}
          onReset={handleMobileReset}
          onApply={handleMobileApply}
        />

        <MobileTabBar 
          activeTab={mobileActiveTab}
          onTabChange={(tab) => {
            setMobileActiveTab(tab);
          }}
          disabled={!selectedImageId}
          isBaseLayerSelected={selectedImageId === 'base-layer'}
        />
      </div>

      {/* 🔥 Sistema de Efeitos - Apenas Lógica (Lazy Loaded) */}
      <Suspense fallback={null}>
        <EffectsManager />
      </Suspense>

      {/* 🔥 Sistema de Filtros de Cores - Apenas Lógica (Lazy Loaded) */}
      <Suspense fallback={null}>
        <ColorFiltersManager />
      </Suspense>

      {/* Painel de Configuração de Papel - Modal Sobreposto */}
      <PaperSizePanel />
      
      {/* Overlay de Loading para Remoção de Fundo - COBRE TODA A TELA */}
      {isBackgroundRemovalLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 shadow-xl max-w-md mx-4 text-center">
            <div className="flex flex-col items-center space-y-4">
              {/* Spinner animado */}
              <div className="relative">
                <div className="w-12 h-12 border-4 border-green-200 rounded-full animate-spin border-t-green-600"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
              </div>
              
              {/* Texto */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">{t('backgroundRemoval.removingBackground')}</h3>
                <p className="text-sm text-gray-600">
                  {t('backgroundRemoval.processingWithAI')}
                </p>
                <p className="text-xs text-gray-500">
                  {t('backgroundRemoval.mayTakeFewSeconds')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Modal de Editor Avançado */}
      <AdvancedEditorModal
        isOpen={isAdvancedModalOpen}
        onClose={closeAdvancedEditor}
        image={advancedEditingImage?.originalSrc || advancedEditingImage?.src}
        initialMask={advancedEditingMask}
        onApply={applyAdvancedEdit}
      />

      {/* 🗑️ Modal de Confirmação de Exclusão */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={confirmDelete}
        onCancel={cancelDeleteConfirmation}
        imageName={imageToDelete?.name || 'Imagem sem nome'}
      />


      {/* Componente de Atalhos de Teclado */}
      <KeyboardShortcuts />
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <AuthProvider>
            <AuthGuard>
              <LanguageProvider>
                <AppProvider>
                  <ConsoleProvider>
                    <AppContent />
                  </ConsoleProvider>
                </AppProvider>
              </LanguageProvider>
            </AuthGuard>
          </AuthProvider>
        } />
      </Routes>
    </Router>
  );
}

export default App;
